import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import './styles.less';
import { useLocation } from 'react-router';
import { ruRU } from '@mui/x-data-grid-pro/locales';
import { DataGridPro } from '@mui/x-data-grid-pro';
import TablePagination from '../../semantic-ui/components/table-pagination';
import { CustomNoRowsOverlay } from './utils';

const DataGridComponent = ({
	headerText,
	onAddButtonClick,
	onImportButtonClick,
	columns,
	rows,
	pagination,
	isTableDataLoading,
	isWithoutHeader,
	isChildren,
	isChildrenWithBorder,
	isInfo,
	isWithoutBoldFirstColumn,
	onDownloadButtonClick,
	customHeaderClassName,
	handleSortChange,
	handleFilterChange,
	getDetailPanelContent,
	customClassName,
	keyTable,
	filterKey,
	sortKey,
	detailPanelHeight,
	contextComponent,
	handleContextMenu,
	isDownloadButtonLoading,
	getRowId,
	style,
	rowHeight,
	columnHeaderHeight,
}) => {
	const { search } = useLocation();

	const query = new URLSearchParams(search);

	keyTable = keyTable ?? 'table-pro';

	if (!isChildren && !style) {
		style = pagination ? { height: 'calc(100vh - 290px)' } : { height: 'calc(100vh - 235px)' };
	}

	const filterModel = () => {
		let filterModel = null;

		if (query.has(filterKey ?? 'filters')) {
			filterModel = JSON.parse(query.get(filterKey ?? 'filters'));
		}

		return filterModel;
	};

	const sortModel = () => {
		let sortModel = [];

		if (query.has(sortKey ?? 'sorts')) {
			sortModel = JSON.parse(query.get(sortKey ?? 'sorts'));
		}

		return sortModel;
	};

	const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState();

	const handleDetailPanelExpandedRowIdsChange = useCallback(newIds => {
		if (newIds.size > 1) {
			const newSet = [];

			const newIdsArray = [...newIds];
			newSet.push(newIdsArray[newIdsArray.length - 1]);
			setDetailPanelExpandedRowIds(new Set(newSet));
		} else {
			setDetailPanelExpandedRowIds(newIds);
		}
	}, []);

	const getDetailPanelHeight = row => {
		const count = row.count ?? 0;
		if (count === 0) return 300;
		return count > 13 ? 540 : (count + 1) * 40;
	};

	const [columnModel, setColumnModel] = useState({});

	const handleColumnWidthChange = params => {
		setColumnModel(prev => ({
			...prev,
			[params.colDef.field]: params.width,
		}));
	};

	const columnsWithSavedWidth = columns.map(col => ({
		...col,
		width: columnModel[col.field] || col.width,
		flex: columnModel[col.field] ? 0 : col.flex,
	}));

	return (
		<div className="data-grid-container__wrapper">
			<div
				className={`${customHeaderClassName || ''} ${
					isWithoutHeader && !customHeaderClassName ? 'data-grid-container__without-header ' : ''
				}${isChildren || isChildrenWithBorder ? 'data-grid-container_children ' : ''}${
					isChildrenWithBorder ? 'data-grid-container_children_with-border ' : ''
				}${isInfo ? 'data-grid-container_info ' : ''}${
					isWithoutBoldFirstColumn ? 'data-grid-container__without-bold-first-column ' : ''
				}data-grid-container`}
			>
				{!isWithoutHeader && !isChildren && (
					<div className="data-grid-container__header">
						<div className="data-grid-container__header-left-side">
							<div className="data-grid-container__header-title">{headerText}</div>
							<div className="data-grid-container__header-buttons">
								{onDownloadButtonClick && (
									<div
										className={`data-grid-container__header-download-button ${
											isDownloadButtonLoading
												? 'data-grid-container__header-download-button_loader'
												: ''
										}`}
										onClick={!isDownloadButtonLoading ? onDownloadButtonClick : null}
									/>
								)}

								{onImportButtonClick && (
									<div
										className="data-grid-container__header-import-button"
										onClick={onImportButtonClick}
									/>
								)}
								{onAddButtonClick && (
									<div className="data-grid-container__header-button" onClick={onAddButtonClick} />
								)}
							</div>
						</div>
					</div>
				)}
				<div className="data-grid-container__table" style={style}>
					<DataGridPro
						key={keyTable}
						initialState={{
							filter: {
								filterModel: filterModel(),
							},
							sorting: { sortModel: sortModel() },
						}}
						slots={{
							noRowsOverlay: CustomNoRowsOverlay,
						}}
						slotProps={{
							loadingOverlay: {
								variant: 'skeleton',
								noRowsVariant: 'skeleton',
							},
							row: {
								onContextMenu: handleContextMenu,
								style: handleContextMenu ? { cursor: 'context-menu' } : {},
							},
						}}
						sx={{
							'& .status-error': {
								backgroundColor: '#ffadad',
								color: '#666',
							},
							'& .status-done': {
								backgroundColor: '#a1f0a6',
								color: '#666',
							},
							'& .status-active': {
								backgroundColor: '#93c9ff',
								color: '#666',
							},
							'& .status-warning': {
								backgroundColor: '#fff093',
								color: '#666',
							},
							'& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
								display: 'none',
							},
						}}
						rows={rows}
						columns={columnsWithSavedWidth}
						onColumnWidthChange={handleColumnWidthChange}
						hideFooterPagination
						hideFooter
						rowHeight={rowHeight || 40}
						columnHeaderHeight={columnHeaderHeight || 40}
						loading={isTableDataLoading}
						sortingMode="server"
						onSortModelChange={handleSortChange}
						filterMode="server"
						filterDebounceMs={300}
						onFilterModelChange={handleFilterChange}
						localeText={ruRU.components.MuiDataGrid.defaultProps.localeText}
						className={`${customClassName || ''} no-border`}
						getDetailPanelContent={getDetailPanelContent}
						detailPanelExpandedRowIds={detailPanelExpandedRowIds}
						onDetailPanelExpandedRowIdsChange={handleDetailPanelExpandedRowIdsChange}
						getDetailPanelHeight={({ row }) => detailPanelHeight ?? getDetailPanelHeight(row)}
						getRowClassName={params => {
							return params.row.status ? `data-grid-container__table__${params.row.status}` : '';
						}}
						disableMultipleColumnsSorting
						getRowId={getRowId || (row => row.id)}
					/>

					{contextComponent}
				</div>
				{pagination && <TablePagination pagination={pagination} isShort />}
			</div>
		</div>
	);
};

DataGridComponent.propTypes = {
	headerText: PropTypes.string,
	onAddButtonClick: PropTypes.func,
	onImportButtonClick: PropTypes.func,
	columns: PropTypes.arrayOf(PropTypes.object).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object),
	pagination: PropTypes.shape({
		currentPage: PropTypes.number,
		totalPages: PropTypes.number.isRequired,
		total: PropTypes.number,
		from: PropTypes.number,
		to: PropTypes.number,
		isNextItem: PropTypes.bool,
		isPrevItem: PropTypes.bool,
		isFirstItem: PropTypes.bool,
		isLastItem: PropTypes.bool,
		onPageChange: PropTypes.func.isRequired,
		onSelectChange: PropTypes.func.isRequired,
		isPaginationTextHidden: PropTypes.bool,
	}),

	isTableDataLoading: PropTypes.bool.isRequired,
	isWithoutHeader: PropTypes.bool,
	isChildren: PropTypes.bool,
	isDownloadButtonLoading: PropTypes.bool,
	isChildrenWithBorder: PropTypes.bool,
	isInfo: PropTypes.bool,
	isWithoutBoldFirstColumn: PropTypes.bool,
	onDownloadButtonClick: PropTypes.func,
	customHeaderClassName: PropTypes.string,
	handleSortChange: PropTypes.func,
	handleFilterChange: PropTypes.func,
	getDetailPanelContent: PropTypes.func,
	customClassName: PropTypes.string,
	keyTable: PropTypes.string,
	filterKey: PropTypes.string,
	sortKey: PropTypes.string,
	detailPanelHeight: PropTypes.number,
	contextComponent: PropTypes.node,
	handleContextMenu: PropTypes.func,
	getRowId: PropTypes.func,
	style: PropTypes.object,
	rowHeight: PropTypes.number,
	columnHeaderHeight: PropTypes.number,
};

export { DataGridComponent };
